/**
 * App context: the API client, the active course, entitlements, and the paywall policy.
 *
 * The paywall policy lives here rather than in each screen, so "never during onboarding"
 * and "max one per session" are properties of the app rather than conventions
 * (docs/08-monetization.md §4).
 */

import { EventTracker, LocalEventStore, type VibeLangClient } from '@vibelang/api-client';
import type { AnalyticsEventName, AnalyticsPayload } from '@vibelang/contracts';
import {
  DISMISS_COOLDOWN_MS,
  type PaywallTrigger,
  type PaywallVariant,
  shouldShowPaywall,
} from '@vibelang/core';
import type { JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * The tracking function, as screens receive it.
 *
 * Exported here rather than defined per screen because several presentational screens
 * take it as a prop — they stay renderable on their own, and the funnel each one
 * measures is visible in its signature.
 */
export type Track = <K extends AnalyticsEventName>(name: K, payload: AnalyticsPayload<K>) => void;

/** Records nothing. A screen test that does not care about analytics says nothing. */
export const noTracking: Track = () => {};

export interface AppState {
  readonly api: VibeLangClient;
  readonly courseId: string | null;
  readonly setCourseId: (id: string | null) => void;
  readonly inOnboarding: boolean;
  readonly setInOnboarding: (value: boolean) => void;
  readonly inActiveSession: boolean;
  readonly setInActiveSession: (value: boolean) => void;
  readonly isSubscribed: boolean;
  readonly setIsSubscribed: (value: boolean) => void;
  readonly hasCompletedLesson: boolean;
  readonly setHasCompletedLesson: (value: boolean) => void;
  /** Returns the variant to render, or null when policy says no. */
  readonly requestPaywall: (trigger: PaywallTrigger) => PaywallVariant | null;
  /**
   * The paywall the policy has allowed, for the single host that renders it.
   *
   * Held here rather than returned and forgotten: every call site dropped the
   * returned variant on the floor, so the policy said yes and nothing appeared. A
   * screen still may not present a paywall itself — that invariant is the reason
   * this is one piece of state and not a prop.
   */
  readonly pendingPaywall: {
    readonly trigger: PaywallTrigger;
    readonly variant: PaywallVariant;
  } | null;
  readonly dismissPaywall: () => void;
  /**
   * Set when a paywall is dismissed, so the host can make the one offer that follows
   * it. Once per session: a decline is a real answer, not a snooze.
   */
  readonly dismissalOffer: boolean;
  readonly clearDismissalOffer: () => void;
  /**
   * Shown once after a purchase, until the entitlement lands. `entitlementReady`
   * false is a real state, not a spinner: receipts are validated server-side, and a
   * client that claims the upgrade before the server agrees is how the competitor's
   * "paid and still locked" reviews happen.
   */
  readonly welcome: {
    readonly tier: string;
    readonly period: 'monthly' | 'yearly';
    readonly renewsAt: number | null;
    readonly entitlementReady: boolean;
  } | null;
  readonly showWelcome: (welcome: {
    tier: string;
    period: 'monthly' | 'yearly';
    renewsAt: number | null;
    entitlementReady: boolean;
  }) => void;
  readonly dismissWelcome: () => void;
  /**
   * The limit sheet, shown where a paywall is refused mid-session.
   *
   * iOS and Android have had this branch since the policy was written — a paywall
   * suppressed during practice becomes a sheet of free alternatives — and web simply
   * showed nothing, so a learner who hit the cap mid-lesson got silence.
   */
  readonly limitSheet: { readonly resetsAt: number } | null;
  readonly dismissLimitSheet: () => void;
  /** A lapsed subscriber's one offer. A decline is a real answer, not a snooze. */
  readonly winback: { readonly lapsedDays: number; readonly offer: string } | null;
  readonly showWinback: (winback: { lapsedDays: number; offer: string }) => void;
  readonly dismissWinback: () => void;
  readonly recordPaywallDismissed: () => void;
  readonly offline: boolean;
  /**
   * Records a product event. Typed from the catalogue, so a missing or misspelled
   * field is a compile error rather than a row nobody can interpret.
   */
  readonly track: Track;
  /** Called when the learner's privacy preference is known or changes. */
  readonly setAnalyticsOptOut: (optedOut: boolean) => void;
}

const AppContext = createContext<AppState | null>(null);

export interface AppProviderProps {
  readonly api: VibeLangClient;
  readonly children: ReactNode;
  readonly initialCourseId?: string | null;
}

export function AppProvider({
  api,
  children,
  initialCourseId = null,
}: AppProviderProps): JSX.Element {
  const [courseId, setCourseId] = useState<string | null>(initialCourseId);
  const [inOnboarding, setInOnboarding] = useState(false);
  const [inActiveSession, setInActiveSession] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasCompletedLesson, setHasCompletedLesson] = useState(false);
  const [pendingPaywall, setPendingPaywall] = useState<{
    trigger: PaywallTrigger;
    variant: PaywallVariant;
  } | null>(null);
  const [dismissalOffer, setDismissalOffer] = useState(false);
  const offeredThisSession = useRef(false);
  const [limitSheet, setLimitSheet] = useState<AppState['limitSheet']>(null);
  const [welcome, setWelcome] = useState<AppState['welcome']>(null);
  const [winback, setWinback] = useState<AppState['winback']>(null);
  const [offline, setOffline] = useState(
    typeof navigator === 'undefined' ? false : !navigator.onLine,
  );

  // Frequency caps are per-session counters held in refs, so a re-render cannot reset
  // them and a screen cannot bypass them.
  const shownThisSession = useRef(0);
  const shownThisWeek = useRef(readWeeklyCount());
  const lastDismissedAt = useRef<number | null>(null);
  const isFirstOpen = useRef(readIsFirstOpen());

  // One tracker for the app's lifetime, with a persisted queue: a lesson finished
  // offline is still counted after a reload.
  const tracker = useMemo(
    () => new EventTracker({ client: api, store: new LocalEventStore() }),
    [api],
  );

  const track = useCallback(
    <K extends AnalyticsEventName>(name: K, payload: AnalyticsPayload<K>) => {
      tracker.track(name, payload);
    },
    [tracker],
  );

  const setAnalyticsOptOut = useCallback(
    (optedOut: boolean) => {
      tracker.setOptedOut(optedOut);
    },
    [tracker],
  );

  useOnlineStatus(setOffline);
  useFlushOnHide(tracker);

  const requestPaywall = useCallback(
    (trigger: PaywallTrigger): PaywallVariant | null => {
      const decision = shouldShowPaywall({
        trigger,
        now: Date.now(),
        inOnboarding,
        inActiveSession,
        isFirstOpen: isFirstOpen.current,
        isSubscribed,
        shownThisSession: shownThisSession.current,
        shownThisWeek: shownThisWeek.current,
        lastDismissedAt: lastDismissedAt.current,
        hasCompletedLesson,
      });

      // `show` without a variant cannot happen by construction, and the narrowing
      // keeps the host from rendering a paywall with no plan on it if it ever did.
      if (!decision.show || decision.variant === null) {
        // Refused mid-practice is not the same as refused: the learner has hit
        // something, and the free ways round it are what the sheet is for.
        if (decision.blockedBy === 'active_session') {
          setLimitSheet({ resetsAt: endOfLocalDay() });
        }
        return null;
      }

      shownThisSession.current += 1;
      shownThisWeek.current += 1;
      writeWeeklyCount(shownThisWeek.current);
      setPendingPaywall({ trigger, variant: decision.variant });
      return decision.variant;
    },
    [inOnboarding, inActiveSession, isSubscribed, hasCompletedLesson],
  );

  const recordPaywallDismissed = useCallback(() => {
    lastDismissedAt.current = Date.now();
  }, []);

  const dismissPaywall = useCallback(() => {
    lastDismissedAt.current = Date.now();
    setPendingPaywall(null);
    // Once per session, and never a second time after it has been answered.
    if (!offeredThisSession.current) {
      offeredThisSession.current = true;
      setDismissalOffer(true);
    }
  }, []);

  const clearDismissalOffer = useCallback(() => setDismissalOffer(false), []);

  const showWelcome = useCallback((next: NonNullable<AppState['welcome']>) => {
    setWelcome(next);
  }, []);
  const dismissWelcome = useCallback(() => setWelcome(null), []);
  const showWinback = useCallback((next: NonNullable<AppState['winback']>) => {
    setWinback(next);
  }, []);
  const dismissWinback = useCallback(() => setWinback(null), []);
  const dismissLimitSheet = useCallback(() => setLimitSheet(null), []);

  const value = useMemo<AppState>(
    () => ({
      api,
      courseId,
      setCourseId,
      inOnboarding,
      setInOnboarding,
      inActiveSession,
      setInActiveSession,
      isSubscribed,
      setIsSubscribed,
      hasCompletedLesson,
      setHasCompletedLesson,
      requestPaywall,
      recordPaywallDismissed,
      pendingPaywall,
      dismissPaywall,
      dismissalOffer,
      clearDismissalOffer,
      limitSheet,
      dismissLimitSheet,
      welcome,
      showWelcome,
      dismissWelcome,
      winback,
      showWinback,
      dismissWinback,
      offline,
      track,
      setAnalyticsOptOut,
    }),
    [
      api,
      courseId,
      inOnboarding,
      inActiveSession,
      isSubscribed,
      hasCompletedLesson,
      requestPaywall,
      recordPaywallDismissed,
      pendingPaywall,
      dismissPaywall,
      dismissalOffer,
      clearDismissalOffer,
      limitSheet,
      dismissLimitSheet,
      welcome,
      showWelcome,
      dismissWelcome,
      winback,
      showWinback,
      dismissWinback,
      offline,
      track,
      setAnalyticsOptOut,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const value = useContext(AppContext);
  if (value === null) throw new Error('useApp must be used inside <AppProvider>');
  return value;
}

export function useApi(): VibeLangClient {
  return useApp().api;
}

export function useCourseId(): string {
  const { courseId } = useApp();
  if (courseId === null) throw new Error('No active course');
  return courseId;
}

// ── Internals ─────────────────────────────────────────────────────────────────

const WEEK_KEY = 'vibelang.paywallWeek';
const FIRST_OPEN_KEY = 'vibelang.opened';

function isoWeek(date = new Date()): string {
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((target.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${week}`;
}

function readWeeklyCount(): number {
  try {
    const raw = globalThis.localStorage?.getItem(WEEK_KEY);
    if (raw === null || raw === undefined) return 0;
    const parsed = JSON.parse(raw) as { week: string; count: number };
    return parsed.week === isoWeek() ? parsed.count : 0;
  } catch {
    return 0;
  }
}

function writeWeeklyCount(count: number): void {
  try {
    globalThis.localStorage?.setItem(WEEK_KEY, JSON.stringify({ week: isoWeek(), count }));
  } catch {
    // Storage unavailable; the session cap still applies.
  }
}

/** Local midnight, which is when a daily cap actually resets for this learner. */
function endOfLocalDay(): number {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
}

function readIsFirstOpen(): boolean {
  try {
    const seen = globalThis.localStorage?.getItem(FIRST_OPEN_KEY);
    if (seen === null || seen === undefined) {
      globalThis.localStorage?.setItem(FIRST_OPEN_KEY, String(Date.now()));
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

function useOnlineStatus(setOffline: (value: boolean) => void): void {
  const bound = useRef(false);
  if (!bound.current && typeof window !== 'undefined') {
    bound.current = true;
    window.addEventListener('online', () => setOffline(false));
    window.addEventListener('offline', () => setOffline(true));
  }
}

/**
 * Flushes the queue when the page goes away.
 *
 * `visibilitychange` to hidden, not `beforeunload`: on mobile Safari and Chrome for
 * Android a backgrounded tab is often never given an unload event at all, so a session's
 * last events — which include every "complete" event worth having — would be left in
 * the queue until the next launch, or lost with the tab.
 */
function useFlushOnHide(tracker: EventTracker): void {
  const bound = useRef(false);
  if (!bound.current && typeof document !== 'undefined') {
    bound.current = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void tracker.flush();
    });
  }
}

export { DISMISS_COOLDOWN_MS };
